const stats = [
  { stat: "10+ Years", label: "Application Security Experience", sub: "Practitioner background, enterprise and regulated environments" },
  { stat: "Practitioner-Led", label: "Senior engineering judgment", sub: "Clear ownership · Evidence-ready outcomes" },
  { stat: "4 Industries", label: "Logistics · Healthcare · Financial Services · SaaS", sub: "Practitioner experience across enterprise verticals" },
  { stat: "3-Phase", label: "Assess. Remediate. Validate.", sub: "Repeatable engineering methodology" },
];

const credibilityBlocks = [
  {
    label: "Active methodology",
    text:
      "Every engagement is executed against the frameworks your auditors and regulators will apply. OWASP LLM Top 10 (2025). OWASP Agentic Security Initiative (2026). MITRE ATLAS v5.4. NIST AI RMF. HIPAA Security Rule 45 CFR 164. HITRUST. SOC 2. CMMC. The frameworks your regulators will apply to your AI program are the frameworks we test against today. Your next audit starts with documentation that already exists.",
  },
  {
    label: "Published research",
    text:
      "ArqeraAI publishes findings from the current AI security threat landscape so clients and prospects can assess our technical judgment before engaging. Recent publications include analysis of CVE-2026-45829, the max-severity ChromaDB authentication bypass affecting AI applications with nearly 14 million monthly PyPI downloads, and the TrapDoor supply chain campaign that weaponized AI coding assistant configuration files to exfiltrate credentials across npm, PyPI, and Crates.io. These are not summaries of other firms' research. They are original practitioner analysis.",
  },
  {
    label: "What a finding looks like",
    text:
      "Findings from recent engagements are documented and published anonymously as permitted by client agreement. The sample below reflects the format, specificity, and regulatory mapping every ArqeraAI client receives.",
  },
];


export default function ArqProofBar() {
  return (
    <section className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="arq-label text-center mb-3">Why Us</div>
        <h2
          className="font-display font-semibold text-arq-text text-center mb-12 max-w-[820px] mx-auto"
          style={{ fontSize: "clamp(26px, 3.4vw, 36px)", lineHeight: 1.2 }}
        >
          Engineering depth. One framework. Outcomes that hold up under scrutiny.
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((s) => (
            <div
              key={s.label}
              className="bg-arq-surface1 border border-arq-border rounded-xl p-6 md:p-7 text-center flex flex-col"
            >
              <div className="font-display font-bold text-[28px] md:text-[32px] text-arq-blue leading-none">
                {s.stat}
              </div>
              <div className="text-arq-text text-[14px] md:text-[15px] font-semibold mt-3 leading-snug">
                {s.label}
              </div>
              <div className="text-arq-muted text-[12px] mt-2 font-mono">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Sample finding cards */}
        <div className="mb-16">
          <div className="arq-label text-center mb-6">What a finding looks like</div>
          <div className="grid md:grid-cols-2 gap-5 max-w-[1100px] mx-auto">
            {[
              {
                tag: "redacted_sample_01",
                severity: "Critical",
                severityClass: "text-arq-danger",
                dotColor: "bg-arq-danger",
                finding: "Indirect prompt injection path in vendor AI integration",
                context: "45 CFR 164.312(a)(1) · HIPAA Access Control",
                exposure: "Unscoped PHI access via downstream model context",
              },
              {
                tag: "redacted_sample_02",
                severity: "High",
                severityClass: "text-[#D97706]",
                dotColor: "bg-[#D97706]",
                finding: "Agent tool-use bypasses logging and approval workflow",
                context: "SOC 2 CC7.2 · NIST AI RMF MEASURE 2.7",
                exposure: "Privileged actions executed without audit trail or human-in-the-loop",
              },
            ].map((f) => (
              <div
                key={f.tag}
                className="bg-arq-bg/80 border border-arq-border rounded-xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.4)]"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-arq-surface1 border-b border-arq-border">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-arq-danger" />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#D97706" }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-arq-teal" />
                  </div>
                  <div className="font-mono text-[11px] text-arq-tertiary">ArqeraAI Assessment · {f.tag}</div>
                </div>
                <div className="p-5 md:p-6 font-mono text-[13px] md:text-[14px]">
                  <div className="grid grid-cols-[90px_1fr] md:grid-cols-[100px_1fr] gap-x-4 gap-y-3">
                    <span className="text-arq-tertiary">Severity</span>
                    <span className={`${f.severityClass} font-semibold`}>{f.severity}</span>

                    <span className="text-arq-tertiary">Finding</span>
                    <span className="text-arq-text">{f.finding}</span>

                    <span className="text-arq-tertiary">Context</span>
                    <span className="text-arq-text">{f.context}</span>

                    <span className="text-arq-tertiary">Exposure</span>
                    <span className="text-arq-text">{f.exposure}</span>

                    <span className="text-arq-tertiary">Status</span>
                    <span className="text-arq-teal font-semibold">Remediated · Validated</span>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-arq-border font-mono text-[11px] text-arq-tertiary">
                  ArqeraAI Assessment · production validation included
                </div>
              </div>
            ))}
          </div>
          <p className="text-arq-tertiary text-[12px] text-center mt-5 max-w-[640px] mx-auto leading-relaxed">
            Every finding documented, mapped to a control, remediated, and validated this way.
          </p>
        </div>




        {/* Framework authority strip */}
        <div className="mb-12">
          <div className="arq-label text-center mb-6">Frameworks we test against</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-[920px] mx-auto">
            {[
              { mark: "OWASP", sub: "LLM Top 10 · 2025" },
              { mark: "MITRE", sub: "ATLAS v5.4" },
              { mark: "NIST", sub: "AI RMF 1.0" },
              { mark: "HIPAA", sub: "45 CFR 164" },
              { mark: "SOC 2", sub: "Trust Services Criteria" },
              { mark: "HITRUST", sub: "CSF v11" },
              { mark: "CMMC", sub: "Level 2 · 2.0" },
              { mark: "ISO", sub: "27001 · 42001" },
            ].map((f) => (
              <div
                key={f.mark}
                className="bg-arq-surface1 border border-arq-border rounded-lg px-4 py-5 text-center"
              >
                <div className="font-display font-bold text-arq-text text-[18px] md:text-[20px] tracking-[0.04em] leading-none">
                  {f.mark}
                </div>
                <div className="font-mono text-[10px] text-arq-tertiary mt-2 tracking-[0.05em]">
                  {f.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credibility blocks */}
        <div className="grid md:grid-cols-1 gap-6 max-w-[900px] mx-auto">
          {credibilityBlocks.map((b) => (
            <div
              key={b.label}
              className="bg-arq-surface1 border border-arq-border rounded-xl p-6 md:p-8"
            >
              <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue mb-3">
                {b.label}
              </div>
              <p className="text-arq-text/90 text-[15px] leading-[1.7]">{b.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
